import type { EventInput } from "@fullcalendar/core";
import { addDays, addHours, format, set, subDays } from "date-fns";

import { parseDate } from "simplydo/core/dates";

import { CalendarResponseEvent } from "./calendar-types";

export function getDefaultActionDate(
  date: Date,
  useClickedTime: boolean,
): Date {
  if (useClickedTime) {
    return set(new Date(date), { seconds: 0, milliseconds: 0 });
  }
  return set(new Date(date), {
    hours: 9,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
}

export function getDefaultEventEndDate(start: Date): Date {
  return addHours(start, 1);
}

/**
 * Store all-day dates as UTC midnight of the user's local calendar day.
 */
export function toAllDayStorageDate(date: Date): Date {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
}

/**
 * Stored all-day (UTC midnight) → local midnight of that UTC calendar day.
 */
export function fromAllDayStorageDate(value: string | Date): Date {
  const date = value instanceof Date ? value : parseDate(value);
  if (!date) {
    return new Date(NaN);
  }
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** YYYY-MM-DD for DateInput / FullCalendar from a stored all-day datetime. */
export function formatAllDayDate(value: string | Date): string {
  const localDay = fromAllDayStorageDate(value);
  if (Number.isNaN(localDay.getTime())) {
    return "";
  }
  return format(localDay, "yyyy-MM-dd");
}

/** DateInput "yyyy-MM-dd" → all-day UTC midnight storage. */
export function parseAllDayInputValue(value: string): Date | null {
  const localDay = parseDate(value);
  if (!localDay) {
    return null;
  }
  return toAllDayStorageDate(localDay);
}

export function isSameAllDayDate(a: string | Date, b: string | Date): boolean {
  const left = a instanceof Date ? a : parseDate(a);
  const right = b instanceof Date ? b : parseDate(b);
  if (!left || !right) {
    return false;
  }
  return (
    left.getUTCFullYear() === right.getUTCFullYear() &&
    left.getUTCMonth() === right.getUTCMonth() &&
    left.getUTCDate() === right.getUTCDate()
  );
}

/** Inclusive end date for an all-day event given FullCalendar's exclusive end. */
export function allDayExclusiveEndToInclusive(exclusiveEnd: Date): Date {
  return toAllDayStorageDate(subDays(exclusiveEnd, 1));
}

/** FullCalendar exclusive end (YYYY-MM-DD) for an inclusive all-day end. */
export function allDayInclusiveEndToExclusiveDateString(
  inclusiveEnd: string | Date,
): string {
  const localDay = fromAllDayStorageDate(inclusiveEnd);
  if (Number.isNaN(localDay.getTime())) {
    return "";
  }
  return format(addDays(localDay, 1), "yyyy-MM-dd");
}

export function isCalendarEventEditable(
  event: CalendarResponseEvent,
  canEdit: boolean,
): boolean {
  if (!canEdit) {
    return false;
  }
  if (event.isGenerated) {
    return false;
  }
  if (event.eventType === "background") {
    return false;
  }
  return event.type === "event";
}

export function toFullCalendarEvents(
  events: CalendarResponseEvent[],
  canEdit: boolean,
): EventInput[] {
  return events.map((event) => {
    const eventEditable = isCalendarEventEditable(event, canEdit);
    const isAllDay = !!event.allDay;

    if (isAllDay && event.from) {
      const inclusiveEnd = event.to || event.from;
      return {
        id: event.id,
        title: event.title || "",
        allDay: true,
        start: formatAllDayDate(event.from),
        end: allDayInclusiveEndToExclusiveDateString(inclusiveEnd),
        backgroundColor: event.color,
        borderColor: event.color,
        display: event.eventType === "background" ? "background" : "auto",
        editable: eventEditable,
        startEditable: eventEditable,
        durationEditable: eventEditable,
        extendedProps: { sourceEvent: event },
      };
    }

    return {
      id: event.id,
      title: event.title || "",
      allDay: false,
      start: event.from,
      end: event.to || undefined,
      backgroundColor: event.color,
      borderColor: event.color,
      display: event.eventType === "background" ? "background" : "auto",
      editable: eventEditable,
      startEditable: eventEditable,
      durationEditable: eventEditable,
      extendedProps: { sourceEvent: event },
    };
  });
}
